import { useEffect, useRef, useState } from 'react';
import { fundingPresets } from '../amount/funding-presets.js';
import { fundingRateIsCurrent } from '../amount/fx.js';
import { isBankFundingMethod } from '../bank-transfers/types.js';
import { isHostedFundingMethod } from '../discovery/policy.js';
import { useFundingCheckout } from './checkout-react.js';
import { useFundingLimitDisplay } from './limit-display-react.js';
/** Shared single-screen wallet checkout. Preparation reserves, but never submits, a payment. */
export function useFundingInlineCheckout(options) {
  const checkout = useFundingCheckout(options);
  const [readyAttempt, setReadyAttempt] = useState(null);
  const walletReady = Boolean(checkout.attemptId && readyAttempt === checkout.attemptId);
  const effectiveOptions = {
    ...options,
    currency: checkout.currency,
    decimals: checkout.decimals,
    ...(checkout.operation?.receiveTarget ? {
      fundingFx: checkout.operation.receiveTarget.fx
    } : {})
  };
  if (checkout.method && isBankFundingMethod(checkout.method)) delete effectiveOptions.fundingFx;
  const displayedLimits = useFundingLimitDisplay(effectiveOptions, checkout);
  const boundary = checkout.preview.providerFailure?.amountBoundary;
  const amountBoundary = boundary && checkout.amountUnits && /^\d+$/.test(checkout.amountUnits) && (boundary.kind === 'minimum' ? BigInt(checkout.amountUnits) < BigInt(boundary.amountUnits) : BigInt(checkout.amountUnits) > BigInt(boundary.amountUnits)) ? boundary : null;
  const handled = useRef(null);
  const latest = useRef(checkout);
  latest.current = checkout;
  const hosted = checkout.method !== null && isHostedFundingMethod(checkout.method);
  const bank = checkout.method === 'sepa';
  const amountEntry = hosted || bank || checkout.method === 'card' ? checkout.state === 'amount' : ['amount', 'review', 'payment'].includes(checkout.state);
  const candidate = checkout.state === 'amount' ? checkout.preview.quote : checkout.quote;
  const candidateKey = candidate ? `${checkout.state}:${checkout.amountUnits}:${candidate.signature}` : null;
  useEffect(() => {
    const current = latest.current;
    if (options.active === false || checkout.purchase && options.directPurchaseEnabled !== true || hosted || bank || checkout.method === 'card' || checkout.busy || checkout.error || !candidateKey || handled.current === candidateKey) return;
    if (current.state === 'amount' && current.preview.review?.state === 'ready' && checkout.canContinueAmount && current.amountUnits) {
      handled.current = candidateKey;
      void current.quoteAmount(current.amountUnits);
    } else if (current.state === 'review' && current.review?.state === 'ready') {
      handled.current = candidateKey;
      void current.pay();
    }
  }, [candidateKey, options.active, options.directPurchaseEnabled, checkout.purchase, checkout.method, checkout.busy, checkout.error, checkout.canContinueAmount, hosted, bank]);
  useEffect(() => {
    if (checkout.state !== 'payment' || hosted || checkout.amountLocked || !checkout.quote || options.active === false) return;
    const timer = setTimeout(() => {
      const current_0 = latest.current;
      if (!current_0.busy && !current_0.amountLocked && current_0.operation) void current_0.quoteAmount(current_0.operation.receiveTarget?.localUnits ?? current_0.operation.principalUnits);
    }, Math.max(0, Math.min(Date.parse(checkout.quote.expiresAt) - Date.now(), 2147483647)));
    return () => clearTimeout(timer);
  }, [checkout.state, checkout.amountLocked, checkout.quote, options.active, hosted]);
  const displayed = checkout.state === 'amount' ? checkout.preview.review : checkout.review;
  return {
    ...checkout,
    amountEntry,
    presets: fundingPresets(checkout.currency, checkout.decimals, effectiveOptions.fundingFx ?? null, Date.now(), amountBoundary ? amountBoundary.kind === 'minimum' ? {
      minimumUnits: amountBoundary.amountUnits
    } : {
      maximumUnits: amountBoundary.amountUnits
    } : undefined),
    amountBoundary,
    walletReady,
    displayedLimits,
    preparingWallet: !bank && amountEntry && !checkout.error && !checkout.preview.error && !checkout.limitRecovery.blocked && (checkout.preview.state === 'loading' || !hosted && checkout.method !== 'card' && !walletReady && (checkout.preview.review?.state === 'ready' || checkout.state === 'review' || checkout.state === 'payment')),
    back() {
      if (hosted && checkout.attemptId && (checkout.state === 'payment' || checkout.state === 'review')) {
        if (checkout.state === 'payment') checkout.hostedEvent({
          kind: 'close'
        });else checkout.viewPaymentStatus();
        return;
      }
      if (amountEntry && !checkout.amountLocked && checkout.method !== 'card') {
        handled.current = null;
        checkout.changeInlineMethod();
      } else checkout.back();
    },
    paymentEvent(event) {
      if (event.kind === 'ready') setReadyAttempt(checkout.attemptId);
      checkout.paymentEvent(event);
    },
    // Disconnected discovery is the entry route, not a child of unavailable methods.
    hasBack: checkout.state !== 'methods' && !(checkout.state === 'connection_required' && checkout.methods.length === 0),
    paymentEnabled: options.active !== false && (!checkout.purchase || options.directPurchaseEnabled === true) && (effectiveOptions.fundingFx === undefined || Boolean(effectiveOptions.fundingFx && fundingRateIsCurrent(effectiveOptions.fundingFx, Date.now()))) && Boolean(checkout.quote && Date.parse(checkout.quote.expiresAt) > Date.now()) && (!checkout.operation?.receiveTarget || fundingRateIsCurrent(checkout.operation.receiveTarget.fx, Date.now())) && checkout.paymentSignature === checkout.quote?.signature && walletReady && checkout.state === 'payment' && !checkout.busy && !checkout.error && checkout.review?.state === 'ready',
    preview: amountEntry && checkout.state !== 'amount' ? {
      ...checkout.preview,
      review: displayed,
      quote: checkout.quote,
      state: 'ready'
    } : checkout.preview,
    previewAmount(units) {
      handled.current = null;
      if (hosted) checkout.previewAmount(units);else checkout.editInlineAmount(units);
    },
    changeMethod() {
      handled.current = null;
      checkout.changeInlineMethod();
    },
    retry() {
      handled.current = null;
      checkout.retry();
    }
  };
}